/**
 * EquityPanel.tsx —— 赢率计算器面板（PRD 模块 4 / 设计方案 4.6，8 个状态全覆盖）
 *
 * 口径红线（不可动摇）：
 *   - 展示的始终是「你 vs 1 名随机对手」的**精确**数值，不随桌上实际人数变化；
 *   - 无采样、无估算、无降级路径：慢只会改文案与配色，绝不改数字来源；
 *   - 文案禁止出现「赢下本手概率 / 多人胜率 / 约 / ± / 采样 / 预计 / 建议…」。
 */

import type { EquityPanelState } from '../state/equityService';
import type { EquitySnapshot } from '../state/equityTypes';
import { pct1, STREET_CN } from '../state/format';
import { Segmented, Tip } from './primitives';

const CALIBRATION = '口径：你 vs 1 名随机对手的精确值（全量枚举，无采样）。该数值不随桌上实际人数变化。';

/**
 * 需求一 FR1-1 / FR1-2（多人桌赢率口径警示）：
 *   - 判定「本桌是否多人局面」的唯一口径 = 本桌总席位数 seatCount ≥ 3；
 *   - N（本桌 N 家）恒取配置的总席位数，不随对手弃牌 / 淘汰实时递减（PRD FR1-1.4 / AC1-6）；
 *   - 单挑桌（seatCount = 2）视为 1v1 即真实胜率，不追加人数、不显示警示行。
 */
export function isMultiway(seatCount: number): boolean {
  return seatCount >= 3;
}

/** 赢率主数字下方常驻警示行文案（FR1-1）。N = 本桌总席位数。 */
export function equityWarnText(seatCount: number): string {
  return `⚠ 1v1 估算，本桌 ${seatCount} 家实战更低`;
}

/**
 * 口径常驻标识（设计方案原则 1）：凡出现赢率数值的容器内，
 * 「对 1 名随机对手」与「仅你可见」必须同屏可见，不允许只收进 tooltip。
 *
 * 需求一 FR1-2：多人桌（seatCount ≥ 3）把「对 1 名随机对手」扩写为
 * 「对 1 名随机对手 · 本桌 N 家」；单挑桌保持原文案（不追加人数后缀）。
 */
export function EquityBadges({ compact = false, seatCount = 2 }: { compact?: boolean; seatCount?: number }) {
  const opponentLabel = isMultiway(seatCount)
    ? `对 1 名随机对手 · 本桌 ${seatCount} 家`
    : '对 1 名随机对手';
  return (
    <p className={`eqp__badges${compact ? ' eqp__badges--compact' : ''}`}>
      <span className="eqp-badge">{opponentLabel}</span>
      <span className="eqp-badge">仅你可见</span>
    </p>
  );
}

/**
 * 需求一 FR1-1：多人桌赢率主数字正下方的常驻警示行。
 *   - 仅 seatCount ≥ 3 且赢率有数值时渲染（由调用方保证只在 result 态挂载）；
 *   - aria-live=polite 只播报 N 变化项、不整段重读（FR1-1、QA-BUG-026 教训）：
 *     文案含唯一变量 N，读屏在 N 变化时才有新内容播报。
 */
function EquityWarnLine({ seatCount }: { seatCount: number }) {
  if (!isMultiway(seatCount)) return null;
  return (
    <p className="eqp__warnline" aria-live="polite">
      {equityWarnText(seatCount)}
    </p>
  );
}

export function EquityPanel({
  state,
  enabled,
  collapsed,
  seatCount,
  onToggleEnabled,
  onToggleCollapse,
  onModeChange,
  onRetry,
  disabledReason,
}: {
  state: EquityPanelState;
  enabled: boolean;
  collapsed: boolean;
  /** 本桌总席位数（含英雄），用于需求一多人桌赢率口径警示（FR1-1 / FR1-2）。 */
  seatCount: number;
  onToggleEnabled: (on: boolean) => void;
  onToggleCollapse: () => void;
  onModeChange: (m: 'live' | 'review') => void;
  onRetry: () => void;
  /** 非空表示当前不可开启（例如已被淘汰进入观战）。 */
  disabledReason: string | null;
}) {
  /**
   * MAJ-3：状态强度必须做到**面板级**可见。
   * 只给内部说明块染底色，在 lg 浮层 / md 横条 / sm 把手这些缩略形态里完全看不出来，
   * 因此把「慢速 / 不可用 / 失败」提升成面板边框（2px 警示 / 2px 错误）。
   * 边框只表状态强度，不表褒贬，与赢率数值的单色蓝五阶互不干扰。
   * 分档按验收要求：慢速 = 2px 黄（仍在算，可等）；暂不可用 / 失败 = 2px 红（此手拿不到数）。
   */
  const tone =
    state.phase === 'error' || state.phase === 'unavailable' ? 'error' : state.phase === 'slow' ? 'warn' : null;

  return (
    <section
      className={`eqp${collapsed ? ' eqp--collapsed' : ''}${tone ? ` eqp--${tone}` : ''}`}
      aria-label="赢率计算器"
      data-phase={state.phase}
    >
      <div className="eqp__head">
        <h2 className="eqp__title">赢率计算器</h2>
        <div className="eqp__head-actions">
          <button
            type="button"
            className="link-btn"
            aria-pressed={enabled}
            disabled={!!disabledReason}
            onClick={() => onToggleEnabled(!enabled)}
          >
            {enabled ? '关闭计算' : '开启计算'}
          </button>
          <button type="button" className="link-btn" onClick={onToggleCollapse} aria-expanded={!collapsed}>
            {collapsed ? '展开' : '收起'}
          </button>
        </div>
      </div>

      {collapsed ? (
        <div className="eqp__collapsed">
          <p className="eqp__collapsed-line">
            {state.phase === 'result' && state.result ? (
              <>
                <span className="eqp__collapsed-label">底池份额期望 </span>
                <b className="num">{pct1(state.result.equity)}</b>
                <span className="eqp__collapsed-street"> · {STREET_CN[state.result.street]}</span>
              </>
            ) : (
              '面板已收起'
            )}
          </p>
          {state.phase === 'result' && state.result ? <EquityBadges compact seatCount={seatCount} /> : null}
        </div>
      ) : (
        <div className="eqp__body">
          {/**
           * QA-BUG-026：改前 aria-live 挂在整个 .eqp__body 上，口径徽标、分段控件、
           * 数值行、口径说明全部在播报范围内，任何一次刷新读屏都要整段重读。
           * 现在只留一条窄粒度状态行：内容只随「状态 / 数值」变化，其余静态结构不再进播报。
           */}
          <p className="sr-only" aria-live="polite">
            {liveSummary(state)}
          </p>

          {disabledReason ? <p className="eqp__note">{disabledReason}</p> : null}

          <EquityBadges seatCount={seatCount} />

          <Segmented
            ariaLabel="计算模式"
            value={state.mode}
            disabled={!enabled}
            onChange={(v) => onModeChange(v)}
            options={[
              { value: 'live' as const, label: '实时' },
              { value: 'review' as const, label: '复盘对照' },
            ]}
          />

          {state.phase === 'off' ? (
            <div className="eqp__state eqp__state--off">
              <p className="eqp__big-muted">未开启</p>
              <p className="eqp__note">
                开启后，每条街的当前牌面会在独立线程里全量枚举，得到你对 1 名随机对手的精确赢率。默认关闭，不影响牌局节奏。
              </p>
            </div>
          ) : null}

          {state.phase === 'silent' ? (
            <div className="eqp__state" aria-busy="true">
              {/* MIN-11：骨架高度对齐真实数值行（34px/1.1 = 37.4px），避免结果落地时整块跳动 */}
              <span className="skeleton" style={{ height: 38, width: '60%', marginBottom: 12 }} />
              <span className="skeleton" style={{ height: 12, width: '100%', marginBottom: 8 }} />
              <span className="skeleton" style={{ height: 12, width: '82%' }} />
              <p className="eqp__note">正在计算当前牌面</p>
            </div>
          ) : null}

          {state.phase === 'loading' || state.phase === 'slow' ? (
            <div className={`eqp__state${state.phase === 'slow' ? ' eqp__state--slow' : ''}`} aria-busy="true">
              <p className="eqp__progress-label">
                {state.phase === 'slow' ? '牌面较多，仍在精确枚举（不会降低精度）' : '正在精确枚举当前牌面'}
              </p>
              {/*
                进度条只在**拿到真实进度**时给 aria-valuenow：
                还没有第一条进度回报时按「不确定进度」渲染（aria-valuetext），
                而不是塞一个 0 —— 那会被读屏念成「0%」，也让自动化断言把假进度当真进度。
              */}
              <div
                className="progress"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                {...(state.progress
                  ? { 'aria-valuenow': Math.round(state.progress.ratio * 100) }
                  : { 'aria-valuetext': '已提交到计算线程，尚无进度回报' })}
              >
                <span
                  className={`progress__bar${state.progress ? '' : ' progress__bar--indeterminate'}`}
                  style={state.progress ? { width: `${state.progress.ratio * 100}%` } : undefined}
                />
              </div>
              <p className="eqp__note num">
                {state.progress
                  ? `${state.progress.boardsEnumerated.toLocaleString('zh-CN')} / ${state.progress.boardsTotal.toLocaleString('zh-CN')} 个完整牌面（${Math.round(state.progress.ratio * 100)}%）`
                  : '已提交到计算线程'}
              </p>
            </div>
          ) : null}

          {state.phase === 'result' && state.result ? (
            <ResultBlock
              snap={toSnap(state)}
              mode={state.mode}
              baseline={state.baseline}
              sawFlop={state.sawFlop}
              seatCount={seatCount}
            />
          ) : null}

          {state.phase === 'cancelled' ? (
            <div className="eqp__state eqp__state--stale">
              <p className="eqp__note">本次计算已取消：{cancelText(state.cancelReason)}</p>
              {state.stale ? (
                <>
                  <p className="eqp__big eqp__big--stale num">{pct1(state.stale.equity)}</p>
                  <p className="eqp__note">
                    以上为 {STREET_CN[state.stale.street]} 节点的上一有效结果，已置灰，不代表当前牌面。
                  </p>
                </>
              ) : (
                <p className="eqp__big-muted">无可用数值</p>
              )}
            </div>
          ) : null}

          {state.phase === 'unavailable' ? (
            <div className="eqp__state eqp__state--warn">
              <p className="eqp__big-muted">暂不可用</p>
              <p className="eqp__note">{state.message ?? '翻牌前离线表校验未通过。'}</p>
              <p className="eqp__note">牌局不受影响，可继续进行；恢复后会自动重新计算。</p>
            </div>
          ) : null}

          {state.phase === 'error' ? (
            <div className="eqp__state eqp__state--warn">
              <p className="eqp__big-muted">计算失败</p>
              <p className="eqp__note">{state.message ?? '计算线程异常。'}</p>
              <button type="button" className="btn" onClick={onRetry}>
                重试
              </button>
            </div>
          ) : null}

          <p className="eqp__calib">
            <Tip text={CALIBRATION} below>
              <span className="eqp__calib-link">口径说明</span>
            </Tip>
          </p>
        </div>
      )}
    </section>
  );
}

function ResultBlock({
  snap,
  mode,
  baseline,
  sawFlop,
  seatCount,
}: {
  snap: EquitySnapshot;
  mode: 'live' | 'review';
  baseline: EquitySnapshot | null;
  sawFlop: boolean;
  seatCount: number;
}) {
  return (
    <div className="eqp__state">
      <div className="eqp__headline">
        <span className="eqp__big num">{pct1(snap.equity)}</span>
        <span className="eqp__big-unit">底池份额期望</span>
      </div>
      {/* 需求一 FR1-1：多人桌赢率主数字正下方常驻警示行（单挑桌自动不渲染） */}
      <EquityWarnLine seatCount={seatCount} />
      <EquityBar value={snap.equity} />
      <dl className="eqp__rows">
        <div>
          <dt>击败对手</dt>
          <dd className="num">{pct1(snap.win)}</dd>
        </div>
        <div>
          <dt>与对手打平</dt>
          <dd className="num">{pct1(snap.tie)}</dd>
        </div>
        <div>
          <dt>被对手击败</dt>
          <dd className="num">{pct1(snap.lose)}</dd>
        </div>
      </dl>

      {mode === 'review' ? (
        <div className="eqp__review">
          <h3 className="eqp__sub">复盘对照</h3>
          {baseline ? (
            <div className="eqp__compare">
              <div>
                <span className="eqp__compare-label">翻牌前</span>
                <span className="num">{pct1(baseline.equity)}</span>
              </div>
              <div>
                <span className="eqp__compare-label">{STREET_CN[snap.street]}</span>
                <span className="num">{pct1(snap.equity)}</span>
              </div>
              <div>
                <span className="eqp__compare-label">变化</span>
                <span className="num">{diffText(snap.equity - baseline.equity)}</span>
              </div>
            </div>
          ) : (
            <p className="eqp__note">{sawFlop ? '本手翻牌前节点没有留下快照。' : '本手尚未见到翻牌，暂无对照数据。'}</p>
          )}
        </div>
      ) : null}

      <p className="eqp__meta num">
        {STREET_CN[snap.street]} ·{' '}
        {snap.fromTable
          ? '命中翻牌前离线精确表'
          : `枚举 ${snap.boardsTotal.toLocaleString('zh-CN')} 个完整牌面`}{' '}
        · {snap.elapsedMs < 1 ? '<1' : snap.elapsedMs.toFixed(1)}ms
      </p>
    </div>
  );
}

/**
 * 单色蓝五阶：只表强度，不表褒贬（设计方案 4.6）。
 *
 * MAJ-2：五阶色值保持不动，但轨道换成中性灰、填充右缘加一道 2px 位置标记 ——
 * 改前 0–20% 档的填充与轨道同为 #EAF1FF（1.00:1 / ΔRGB 0），长度信息完全读不出来。
 * 现在低档也能看清边界，且不引入任何红绿/褒贬暗示。
 */
function EquityBar({ value }: { value: number }) {
  const step = value >= 0.8 ? 5 : value >= 0.6 ? 4 : value >= 0.4 ? 3 : value >= 0.2 ? 2 : 1;
  return (
    <div className="eqbar" aria-hidden="true" data-step={step}>
      <span className="eqbar__fill" style={{ width: `${Math.min(100, value * 100)}%`, background: `var(--equity-${step})` }} />
    </div>
  );
}

/**
 * 读屏窄粒度播报文本（QA-BUG-026）。
 * 只描述「当前状态 + 当前数值」，用词与界面一致但不与任何可见文案完全重复，
 * 避免读屏在浏览模式下把同一句读两遍。
 */
function liveSummary(s: EquityPanelState): string {
  switch (s.phase) {
    case 'result':
      return s.result ? `底池份额期望 ${pct1(s.result.equity)}，${STREET_CN[s.result.street]}` : '';
    case 'off':
      return '赢率计算器未开启';
    case 'silent':
    case 'loading':
      return '正在精确枚举';
    case 'slow':
      return '牌面较多，仍在精确枚举';
    case 'cancelled':
      return '本次计算已取消';
    case 'unavailable':
      return '赢率计算暂不可用';
    case 'error':
      return '赢率计算失败';
    default:
      return '';
  }
}

function toSnap(s: EquityPanelState): EquitySnapshot {
  const r = s.result!;
  return {
    street: r.street,
    win: r.win,
    tie: r.tie,
    lose: r.lose,
    equity: r.equity,
    fromTable: r.fromTable,
    elapsedMs: r.elapsedMs,
    boardsTotal: r.boardsTotal,
  };
}

function diffText(d: number): string {
  const sign = d > 0 ? '+' : d < 0 ? '−' : '';
  return `${sign}${(Math.abs(d) * 100).toFixed(1)}pp`;
}

function cancelText(reason: string | null): string {
  switch (reason) {
    case 'new_community_card':
      return '牌面已更新';
    case 'hand_ended':
      return '本手已结束';
    case 'player_acted':
      return '你已完成行动';
    case 'calculator_closed':
      return '计算器已关闭';
    case 'superseded':
      return '已有新的计算请求';
    default:
      return '外部中止';
  }
}
